import axios from 'axios'
import API from '@/api/base'
import baseDomain from '@/api/base-domain'
import {
  getSavedState,
  saveState,
  setDefaultAuthHeaders,
} from '@utils/auth-helpers'

// Define Model
API.define(
  'passwordReset',
  {
    email: '',
    site: {
      jsonApi: 'hasOne',
      type: 'site',
    },
  },
  {
    collectionPath: 'customers/password_reset',
  }
)

export const state = {
  currentUserId: getSavedState('socC2'),
  currentUser: null,
  currentToken: getSavedState('socC3'),
}

export const mutations = {
  SET_CURRENT_USER_ID(state, newValue) {
    state.currentUserId = newValue
    saveState('socC2', newValue)
    setDefaultAuthHeaders(state)
  },
  SET_CURRENT_TOKEN(state, newValue) {
    state.currentToken = newValue
    saveState('socC3', newValue)
    setDefaultAuthHeaders(state)
  },
  SET_CURRENT_USER(state, user) {
    state.currentUser = user
  },
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn: (state) => !!state.currentUserId,
  userId: (state) => state.currentUserId,
  sessionAuthenticated: (state) => !!state.currentToken,
  getUser: (state) => state.currentUser,
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  // Do not use for any actions that require a token or siteId because they
  // are not available when this function runs
  init({ state, dispatch }) {},
  // Log in the user
  authenticateCustomer: ({ commit, dispatch }, { email, password }) =>
    new Promise((resolve, reject) => {
      axios
        .post(`${baseDomain}/v2/sessions`, {
          session: {
            customer_email: email,
            password: password,
          },
        })
        .then((response) => {
          const customer = response.data.data.customer.attributes
          // Store the user and user id
          commit('SET_CURRENT_USER_ID', customer.id)
          commit('SET_CURRENT_USER', customer)
          // Pre-populate customer forms with customer information
          dispatch('customers/setCustomerForm', customer, {
            root: true,
          })
          resolve(customer)
        })
        .catch((error) => {
          reject(error)
        })
    }),

  passwordReset: ({ commit }, params) =>
    new Promise((resolve, reject) => {
      API.create('passwordReset', {
        email: params.email,
        site: {
          id: params.site,
        },
      })
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    }),

  saveUser: ({ commit, dispatch }, user) => {
    let userId = null
    if (user) userId = user.id
    // Persist user id
    commit('SET_CURRENT_USER_ID', userId)
    commit('SET_CURRENT_USER', user)
    // Pre-populate customer forms with customer information
    if (userId) {
      dispatch('customers/setCustomerForm', user, {
        root: true,
      })
    } else {
      dispatch(
        'customers/resetPrimaryForm',
        {},
        {
          root: true,
        }
      )
    }
  },

  createSession({ commit, dispatch, getters }, { apiKey } = {}) {
    // if (getters.sessionAuthenticated) return dispatch('validate')

    return new Promise((resolve) => {
      axios
        .post(`${baseDomain}/v2/sessions`, {
          session: {
            key: apiKey,
          },
        })
        .then((response) => {
          const token = response.data.data.access_token
          commit('SET_CURRENT_TOKEN', token)
          axios.defaults.headers.common.Authorization = token
          API.headers.Authorization = `Bearer ${token}`
          resolve(token)
        })
    })
  },

  // Logs out the current user.
  logOut({ commit, dispatch }) {
    commit('SET_CURRENT_USER_ID', null)
    commit('SET_CURRENT_USER', null)
    dispatch(
      'customers/resetPrimaryForm',
      {},
      {
        root: true,
      }
    )
  },

  updateCustomer: ({ commit, dispatch }, customer) =>
    new Promise((resolve, reject) => {
      API.update('customer', customer)
        .then((response) => {
          const updatedCustomer = response.data
          dispatch('saveUser', updatedCustomer)
          resolve(updatedCustomer)
        })
        .catch((error) => {
          reject(error)
        })
    }),

  // Validates the current user's token and refreshes it
  // with new data from the API.
  // TODO: (CALE) Update to work with SB
  validate({ commit, state }) {
    if (!state.currentUserId) return Promise.resolve(null)

    return axios
      .get('/api/session')
      .then((response) => {
        const user = response.data
        commit('SET_CURRENT_USER', user)
        return user
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          commit('SET_CURRENT_USER', null)
        }
        return null
      })
  },
}

// ===
// Private helpers
// ===

// fetchCustomer: ({ commit, dispatch }, userId) =>
// new Promise((resolve, reject) => {
//   API.find('customer', userId)
//     .then((response) => {
//       console.log(response)
//       // TODO: Finish this
//       // commit('SET_CURRENT_USER', )
//       resolve()
//     })
//     .catch((error) => {
//       reject(error.response)
//     })

// }),

// 2 hours * 60 minutes * 60 seconds * 1000 ms = 7200000 ms
// const userSessionDuration = 7200000
// ===
// Private DOC helpers
// ===
// function saveStateDOC(key, state) {
//   let bundle = null
//   if (state) {
//     bundle = {
//       payload: state,
//       expiry: new Date().getTime() + userSessionDuration,
//     }
//   }
//   cookieService.set(key, JSON.stringify(bundle), '2h')
// }
